// @flow

/**
 * Prompt Chat Context Hooks
 * Custom hooks for accessing chat state and dispatch
 */

import { useContext } from 'react';
import { 
  PromptChatStateContext, 
  PromptChatDispatchContext,
  StreamingAdapterContext,
  FeedbackServiceContext,
} from './PromptChatProvider';
import { DownloadActionsContext } from './DownloadActionsContext';
import { CustomTagsContext } from './CustomTagsContext';
import { OrganisationContext, useOrganisation } from './OrganisationContext';

import type { ChatState, PromptChatAction, StreamingHookResult, FeedbackService, DownloadAction } from '../types';
import type { CustomTagsContextValue } from './CustomTagsContext';

/**
 * Hook to access chat state
 * @param {Function} select - Optional selector function to extract specific state slice
 * @returns {Object} Chat state or selected slice
 */
export function usePromptChatState<T>(select?: (state: ChatState) => T): ChatState | T {
  const context = useContext(PromptChatStateContext);
  if (!context) {
    throw new Error('usePromptChatState must be used within a PromptChatProvider');
  }
  return select ? select(context) : context;
}

/**
 * Hook to access chat dispatch function
 * @returns {Function} Dispatch function for chat actions
 */
export function usePromptChatDispatch(): (action: PromptChatAction) => void {
  const context = useContext(PromptChatDispatchContext);
  if (!context) {
    throw new Error('usePromptChatDispatch must be used within a PromptChatProvider');
  }
  return context;
}

/**
 * Hook to access streaming adapter
 * @returns {StreamingHookResult} Streaming adapter instance
 */
export function useStreamingAdapter(): StreamingHookResult {
  const context = useContext(StreamingAdapterContext);
  if (!context) {
    throw new Error('useStreamingAdapter must be used within a PromptChatProvider');
  }
  return context;
}

/**
 * Hook to access feedback service (optional)
 * @returns {FeedbackService | null} Feedback service instance or null
 */
export function useFeedbackService(): ?FeedbackService {
  return useContext(FeedbackServiceContext);
}

/**
 * Hook to access download actions configuration
 * @returns {DownloadAction[] | null} Array of download actions or null
 */
export function useDownloadActions(): ?DownloadAction[] {
  return useContext(DownloadActionsContext);
}

/**
 * Hook to access custom tags context
 * @returns {CustomTagsContextValue | null} Custom tags context or null
 */
export function useCustomTags(): ?CustomTagsContextValue {
  return useContext(CustomTagsContext);
}

// Re-export context for provider usage
export { CustomTagsContext };
export { OrganisationContext, useOrganisation };
export { useFeedbackServiceWithMetadata } from '../hooks/useFeedbackServiceWithMetadata';
